import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Eye, EyeOff, KeyRound, Loader2, Home, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { PrimaryLogo } from "@/components/Brand";
import { toast } from "sonner";

export default function AdminResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("This reset link is missing its token. Please request a new one.");
    }
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    if (pw.length < 12) {
      toast.error("Password must be at least 12 characters.");
      return;
    }
    if (pw !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/reset-password", {
        token,
        new_password: pw,
        confirm_password: confirm,
      });
      setDone(true);
      toast.success("Password updated. Please sign in with your new password.");
      setTimeout(() => navigate("/admin/login", { replace: true }), 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "We couldn't reset your password. Please request a new link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: "#F4F8FB" }}>
      <div className="max-w-md w-full">
        <Link to="/" className="inline-flex items-center gap-2 mb-6 text-sm" style={{ color: "#0B2D59" }}>
          <Home className="w-4 h-4" /> Back to home
        </Link>
        <div className="flex justify-center mb-6">
          <PrimaryLogo maxWidth={240} />
        </div>
        <div className="p2p-card p-8">
          <div className="flex items-center gap-3 mb-2">
            <KeyRound className="w-6 h-6" style={{ color: "#0B2D59" }} />
            <h1 className="font-serif text-2xl" style={{ color: "#0B2D59" }}>Choose a new password</h1>
          </div>
          <p className="text-sm" style={{ color: "#475569" }}>
            Enter a new password for your administrator account. Use at least 12 characters.
          </p>

          {done ? (
            <div className="mt-6 rounded-lg p-4 flex items-start gap-3" style={{ background: "#ECF3EC", color: "#2A4535" }}>
              <CheckCircle2 className="w-5 h-5 mt-0.5" />
              <div className="text-sm">Password updated. Redirecting you to sign in…</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4 mt-6" data-testid="reset-password-form">
              <div>
                <label htmlFor="new-pw" className="p2p-label">New password</label>
                <div className="relative">
                  <input
                    id="new-pw"
                    type={show ? "text" : "password"}
                    className="p2p-input pr-12"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    required
                    minLength={12}
                    autoComplete="new-password"
                    data-testid="reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    aria-label={show ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full"
                    style={{ color: "#475569" }}
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="confirm-pw" className="p2p-label">Confirm new password</label>
                <input
                  id="confirm-pw"
                  type={show ? "text" : "password"}
                  className="p2p-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={12}
                  autoComplete="new-password"
                  data-testid="reset-confirm-password"
                />
              </div>
              <button
                type="submit"
                className="p2p-btn-primary w-full inline-flex items-center justify-center gap-2"
                disabled={busy || !token}
                data-testid="reset-submit-btn"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {busy ? "Updating…" : "Update password"}
              </button>
              <p className="text-xs" style={{ color: "#5C4A3D" }}>
                Reset links expire 30 minutes after they are requested and can only be used once.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
